import { DASHBOARD_API_BASE_URL, USE_DASHBOARD_REAL_BACKEND, type DashboardApiResponse } from '../config/config'
import { ADMIN_TOKEN_KEY, ADMIN_USER_KEY, logoutTemporaryAdmin } from '../../auth/adminAuth'

export type ProfileDetails = {
  firstName: string
  lastName: string
  email: string
  phone: string
  role: string
  country: string
  avatar?: string
}

export const DEFAULT_PROFILE: ProfileDetails = {
  firstName: 'Admin',
  lastName: 'Admin',
  email: 'admin@salzburgcab.com',
  phone: '+43 677 12345678',
  role: 'Super Administrator',
  country: 'Austria',
}

const PROFILE_STORAGE_KEY = 'salzburg_dashboard_profile_details'
const PROFILE_PHOTO_STORAGE_KEY = 'salzburg_dashboard_profile_photo'
const PRIVACY_STORAGE_KEY = 'salzburg_dashboard_privacy_policy'
const TERMS_STORAGE_KEY = 'salzburg_dashboard_terms_conditions'

const notifyAdminProfileUpdated = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('admin-profile-updated'))
  }
}

const getAuthHeaders = (): HeadersInit => {
  const token = typeof window !== 'undefined' ? localStorage.getItem(ADMIN_TOKEN_KEY) : null
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

const profileFromBackend = (value: any): ProfileDetails => ({
  firstName: value?.firstName ?? value?.first_name ?? '',
  lastName: value?.lastName ?? value?.last_name ?? '',
  email: value?.email ?? '',
  phone: value?.phone ?? value?.phone_number ?? '',
  role: value?.role ?? (value?.is_staff || value?.is_superuser ? 'Admin' : ''),
  country: value?.country ?? '',
  ...(value?.avatar || value?.photo || value?.profile_photo
    ? { avatar: value.avatar ?? value.photo ?? value.profile_photo }
    : {}),
})

const getResponseData = (result: any) =>
  result?.data?.profile ||
  result?.data?.user ||
  result?.profile ||
  result?.user ||
  result?.data ||
  result

const backendError = (result: any, fallback: string) => {
  if (typeof result?.detail === 'string') return result.detail
  if (typeof result?.message === 'string') return result.message
  if (result && typeof result === 'object') {
    const firstError = Object.values(result).find(
      (value) => Array.isArray(value) && value.length
    )
    if (Array.isArray(firstError)) return String(firstError[0])
  }
  return fallback
}

/**
 * Fetch Profile API request (GET)
 */
export const apiGetProfile = async (): Promise<DashboardApiResponse<ProfileDetails>> => {
  if (!USE_DASHBOARD_REAL_BACKEND) {
    try {
      const saved = localStorage.getItem(PROFILE_STORAGE_KEY)
      const photo = localStorage.getItem(PROFILE_PHOTO_STORAGE_KEY)
      const profile = saved
        ? { ...DEFAULT_PROFILE, ...JSON.parse(saved) }
        : DEFAULT_PROFILE

      return {
        success: true,
        message: 'Profile loaded',
        data: photo ? { ...profile, avatar: photo } : profile,
      }
    } catch {
      return {
        success: true,
        message: 'Default profile loaded',
        data: DEFAULT_PROFILE,
      }
    }
  }

  try {
    const response = await fetch(`${DASHBOARD_API_BASE_URL}/api/admin/profile`, {
      method: 'GET',
      headers: getAuthHeaders(),
    })

    const result = await response.json().catch(() => null)

    if (response.status === 401) logoutTemporaryAdmin()

    if (!response.ok) {
      return {
        success: false,
        message: backendError(result, 'Unable to load profile.'),
      }
    }

    return {
      success: true,
      message: 'Profile fetched successfully',
      data: profileFromBackend(getResponseData(result)),
    }
  } catch {
    return {
      success: false,
      message: 'Unable to connect to the backend.',
    }
  }
}

/**
 * Update Profile API request (PUT / POST)
 */
export const apiUpdateProfile = async (
  payload: ProfileDetails
): Promise<DashboardApiResponse<ProfileDetails>> => {
  if (!USE_DASHBOARD_REAL_BACKEND) {
    localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(payload))
    localStorage.setItem(
      ADMIN_USER_KEY,
      JSON.stringify({
        first_name: payload.firstName,
        last_name: payload.lastName,
        email: payload.email,
        phone: payload.phone,
        role: payload.role,
        avatar: payload.avatar,
      }),
    )
    notifyAdminProfileUpdated()
    return {
      success: true,
      message: 'Profile updated successfully',
      data: payload,
    }
  }

  try {
    const response = await fetch(`${DASHBOARD_API_BASE_URL}/api/admin/profile`, {
      method: 'PATCH',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        first_name: payload.firstName,
        last_name: payload.lastName,
        email: payload.email,
        phone: payload.phone,
        country: payload.country,
      }),
    })

    const result = await response.json().catch(() => null)

    if (response.status === 401) logoutTemporaryAdmin()

    if (!response.ok) {
      return {
        success: false,
        message: backendError(result, 'Profile update failed.'),
      }
    }

    const data = profileFromBackend(getResponseData(result))

    localStorage.setItem(
      ADMIN_USER_KEY,
      JSON.stringify(getResponseData(result))
    )
    notifyAdminProfileUpdated()

    return {
      success: true,
      message: result?.message || 'Profile updated successfully',
      data,
    }
  } catch {
    return {
      success: false,
      message: 'Unable to connect to the backend.',
    }
  }
}

/**
 * Update Password API request (POST)
 */
export const apiUpdateAdminPassword = async (payload: {
  currentPassword: string
  newPassword: string
  confirmPassword?: string
}): Promise<DashboardApiResponse> => {
  if (!USE_DASHBOARD_REAL_BACKEND) {
    return {
      success: true,
      message: 'Password updated successfully',
    }
  }

  try {
    const response = await fetch(
      `${DASHBOARD_API_BASE_URL}/api/admin/profile/change-password`,
      {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_password: payload.currentPassword,
          new_password: payload.newPassword,
          confirm_password: payload.confirmPassword,
        }),
      }
    )

    const result = await response.json().catch(() => null)

    if (response.status === 401) logoutTemporaryAdmin()

    if (!response.ok) {
      return {
        success: false,
        message: backendError(result, 'Password update failed'),
      }
    }

    return {
      success: true,
      message: result?.message || 'Password updated successfully',
    }
  } catch {
    return {
      success: false,
      message: 'Unable to connect to the backend.',
    }
  }
}

/**
 * Upload / Update Profile Avatar (POST)
 */
export const apiUploadProfilePhoto = async (
  photo: File
): Promise<DashboardApiResponse<{ photoUrl: string }>> => {
  if (!USE_DASHBOARD_REAL_BACKEND) {
    return new Promise((resolve) => {
      const reader = new FileReader()

      reader.addEventListener('load', () => {
        const photoUrl =
          typeof reader.result === 'string'
            ? reader.result
            : ''

        if (photoUrl) {
          localStorage.setItem(PROFILE_PHOTO_STORAGE_KEY, photoUrl)
        }

        resolve({
          success: true,
          message: 'Photo updated successfully',
          data: { photoUrl },
        })
      })

      reader.readAsDataURL(photo)
    })
  }

  try {
    const formData = new FormData()
    formData.append('photo', photo)

    const response = await fetch(
      `${DASHBOARD_API_BASE_URL}/api/admin/profile/avatar`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      }
    )

    const result = await response.json().catch(() => null)

    if (response.status === 401) logoutTemporaryAdmin()

    if (!response.ok) {
      return {
        success: false,
        message: backendError(result, 'Photo upload failed.'),
      }
    }

    const responseData = getResponseData(result)

    const photoUrl =
      responseData?.photoUrl ||
      responseData?.photo_url ||
      responseData?.avatar

    return {
      success: true,
      message: result?.message || 'Photo uploaded successfully',
      data: photoUrl ? { photoUrl } : undefined,
    }
  } catch {
    return {
      success: false,
      message: 'Unable to connect to the backend.',
    }
  }
}

/**
 * Update Legal Settings (Privacy Policy & Terms)
 */
export const apiUpdateLegalSettings = async (payload: {
  privacy?: string
  terms?: string
}): Promise<DashboardApiResponse> => {
  if (payload.privacy !== undefined) {
    localStorage.setItem(PRIVACY_STORAGE_KEY, payload.privacy)
  }
  if (payload.terms !== undefined) {
    localStorage.setItem(TERMS_STORAGE_KEY, payload.terms)
  }

  try {
    const response = await fetch(`${DASHBOARD_API_BASE_URL}/api/admin/settings/legal`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    }).catch(() => null)

    if (response && response.ok) {
      const result = await response.json().catch(() => null)
      return {
        success: true,
        message: result?.message || 'Settings saved successfully',
      }
    }
  } catch {}

  return {
    success: true,
    message: 'Settings saved successfully',
  }
}